import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { number, object, string } from 'yup';

import { useAlerta, useToast } from 'Component/Alerta';
import {
  AnimacaoCartaoDeCredito,
  AnimacaoConfirmacao,
} from 'Component/Animacao';
import Botao from 'Component/Botao';
import BotaoLink from 'Component/BotaoLink';
import Form, { CaixaDeSelecao, Campo, Contexto, Selecao } from 'Component/Form';
import Icone from 'Component/Icone';
import PassoAPasso, { Passo, usePassoAPasso } from 'Component/PassoAPasso';
import Certificado, { RetornoCertificado } from 'Interface/Certificados';
import Curso from 'Interface/Cursos';
import { Erro } from 'Interface/Requisicao';
import { CartaoDeCredito } from 'Interface/Transacoes';
import Usuario from 'Interface/Usuarios';
import {
  useAdicionarCertificadoMutation,
  useCalcularFretesQuery,
  useEditarPerfilMutation,
  useRecuperarCursosQuery,
  useRecuperarPerfilQuery,
} from 'Service';
import {
  BANDEIRAS,
  DENOMINACOES_DO_CERTIFICADO,
  ESTADOS,
  FRETES,
  MASCARA_CEP,
  MASCARA_DATA_VENCIMENTO,
} from 'View/constants';
import { recuperarMensagemDeErro } from 'View/functions';
import { useRecuperarCepQuery } from 'View/Service/viacep';

const regrasPagamento = object({
  frete: number().required('Obrigatório'),
  nome: string().required('Obrigatório'),
  numero: number().required('Obrigatório'),
  vencimento: string().required('Obrigatório'),
  codigo: number().required('Obrigatório'),
  bandeira: string().required('Obrigatório'),
});

const Adicionar = (): React.ReactElement => {
  const toast = useToast();
  const alerta = useAlerta();
  const { slug = '', formato = '' } = useParams();

  const [url, setUrl] = useState<string>('');
  const [cep, setCep] = useState<string>('');
  const [pagamento, setPagamento] = useState<
    CartaoDeCredito & { frete: number }
  >();

  const [passo, setPasso] = usePassoAPasso();
  const [editarPerfil] = useEditarPerfilMutation();
  const [adicionarCertificado] = useAdicionarCertificadoMutation();

  const { data: { usuario } = {} } = useRecuperarPerfilQuery();
  const { data: { cursos: [curso] } = { cursos: [{} as Curso] } } =
    useRecuperarCursosQuery({
      finder: 'slug',
      contain: ['Categorias', 'Modulos.Aulas.AulaConcluida'],
      options: { slug: { slug: slug } },
    });
  const { data: endereco } = useRecuperarCepQuery(cep, {
    skip: cep && cep.length == 9 ? false : true,
  });
  const { data: { fretes } = {}, refetch: recalcularFrete } =
    useCalcularFretesQuery();

  const enviarPerfil = async (dados: Usuario): Promise<void> => {
    try {
      await editarPerfil(dados).unwrap();

      setPasso(formato == 'digital' ? 3 : 2);

      recalcularFrete();
    } catch (erro) {
      toast({
        titulo: 'Erro ao editar seu perfil',
        conteudo: 'Verifique os logs para maiores detalhes.',
        tema: 'danger',
      });
    }
  };

  const salvarPagamento = (dados: CartaoDeCredito & { frete: number }) => {
    setPagamento(dados);

    setPasso(3);
  };

  const enviarCertificado = async (dados: Certificado): Promise<void> => {
    try {
      const resposta = (await adicionarCertificado({
        ...dados,
        curso_id: curso.id,
        formato: formato == 'digital' ? 1 : 2,
        ...(pagamento
          ? { pagamento: { frete: pagamento.frete, cartao: { ...pagamento } } }
          : {}),
      }).unwrap()) as unknown as RetornoCertificado;

      setUrl(resposta.certificado.url);

      setPasso(4);
    } catch (erro) {
      const { data } = erro as Erro;

      alerta({
        titulo: 'Erro',
        conteudo: data.errors
          ? recuperarMensagemDeErro(data.errors)
          : data.message,
        tema: 'danger',
      });
    }
  };

  return (
    <div className="animate__animated animate__fadeIn">
      <div
        className="hf-400 d-flex align-items-end"
        style={{
          backgroundImage: `linear-gradient(0deg, rgba(0, 0, 0, 1) 0%, rgba(0, 0, 0, 0.5) 100%), url(${curso.imagem})`,
          backgroundRepeat: 'no-repeat',
          backgroundPosition: 'center',
          backgroundSize: 'cover',
          marginTop: '-130px',
        }}
      >
        <div className="container text-center">
          <h1 className="display-4 text-white">
            <small className="d-block text-secondary fs-3 fw-bold">
              Emissão do certificado de
            </small>
            {curso.nome}
          </h1>
          <p className="lead text-muted">
            Parabéns por estar adquirindo seu certificado!
          </p>
        </div>
      </div>
      <div className="container mb-5 mt-5">
        <div className="row justify-content-center px-3 px-sm-0">
          <div className="col-md-12">
            {!formato ? (
              <div className="row justify-content-center">
                <div className="col-md-5">
                  <p className="lead text-white text-center">
                    Para continuar você deve escolher como deseja seu{' '}
                    <span className="fw-bold text-secondary">certificado</span>.
                    Você pode optar somente pela via digital, sem custo algum,
                    ou optar pela via física impressa que possui uma taxa de
                    emissão e envio.
                  </p>
                  <div className="hstack flex-column flex-sm-row gap-3 mx-auto justify-content-center">
                    <BotaoLink
                      lg
                      to={`/estudar/curso/${curso.slug}/certificado/digital`}
                    >
                      <React.Fragment>
                        Somente via digital
                        <br />
                        <small className="fs-6 fw-lighter">(Sem custo)</small>
                      </React.Fragment>
                    </BotaoLink>
                    <BotaoLink
                      lg
                      tema="secondary"
                      to={`/estudar/curso/${curso.slug}/certificado/fisico`}
                    >
                      <React.Fragment>
                        Quero também a via física
                        <br />
                        <small className="fs-6 fw-lighter">
                          (Possui custo)
                        </small>
                      </React.Fragment>
                    </BotaoLink>
                  </div>
                </div>
              </div>
            ) : (
              <div className="animate__animated animate__fadeIn">
                <PassoAPasso ativo={passo}>
                  <Passo chave={1}>
                    <div className="row justify-content-center">
                      <div className="col-md-5">
                        <Form onSubmit={enviarPerfil} inicial={usuario}>
                          <p className="h3 mb-3 text-white text-center">
                            <Icone nome="person-check" className="me-3" />
                            Confirme seus dados
                          </p>
                          <p className="lead mb-4 text-muted text-center">
                            Para que possamos emitir seu certificado,
                            primeiramente precisamos que você confirme alguns de
                            seus dados para que não seja emitido de forma
                            errada.
                          </p>
                          <div className="bg-white rounded p-4">
                            <Campo name="nome" rotulo="Nome completo" />
                            <Campo name="rg" rotulo="Número do RG" />
                            <div className="row">
                              <div className="col-6">
                                <Campo
                                  name="orgao_emissor"
                                  rotulo="Orgão emissor"
                                  semMargem
                                />
                              </div>
                              <div className="col-6">
                                <Campo
                                  name="estado_orgao_emissor"
                                  rotulo="Estado emissor"
                                  semMargem
                                />
                              </div>
                            </div>
                            {formato == 'fisico' && (
                              <React.Fragment>
                                <p className="text-center fs-4 fw-bold mt-3">
                                  Endereço para envio{' '}
                                  <Icone nome="truck" className="ms-3" />
                                </p>
                                <div className="row mt-3">
                                  <div className="col-md-6">
                                    <Campo
                                      name="cep"
                                      rotulo="CEP"
                                      prefixar={<Icone nome="geo-fill" />}
                                      aoAtualizar={setCep}
                                      mascara={MASCARA_CEP}
                                    />
                                  </div>
                                  <div className="col-md-6">
                                    <Selecao
                                      name="estado"
                                      rotulo="Estado onde reside"
                                      opcoes={ESTADOS}
                                      forcarValor={endereco?.uf}
                                    />
                                  </div>
                                </div>
                                <Campo
                                  name="cidade"
                                  rotulo="Cidade onde reside"
                                  forcarValor={endereco?.localidade}
                                />
                                <Campo
                                  name="bairro"
                                  rotulo="Bairro onde reside"
                                  forcarValor={endereco?.bairro}
                                />
                                <div className="row">
                                  <div className="col-md-8">
                                    <Campo
                                      name="endereco"
                                      rotulo="Endereço de onde reside"
                                      forcarValor={endereco?.logradouro}
                                    />
                                  </div>
                                  <div className="col-md-4">
                                    <Campo
                                      name="numero"
                                      type="number"
                                      rotulo="Número"
                                    />
                                  </div>
                                </div>
                                <Campo
                                  name="complemento"
                                  rotulo="Complemento"
                                  semMargem
                                />
                              </React.Fragment>
                            )}
                          </div>
                          <div className="hstack flex-column flex-sm-row gap-3 mx-auto justify-content-center mt-5">
                            <Botao lg type="submit">
                              Eu confirmo meus dados
                            </Botao>
                            <BotaoLink
                              lg
                              to="/estudar/meus-estudos"
                              tema="outline-light"
                            >
                              Deixar para depois
                            </BotaoLink>
                          </div>
                        </Form>
                      </div>
                    </div>
                  </Passo>
                  <Passo chave={2}>
                    <Form
                      onSubmit={salvarPagamento}
                      inicial={pagamento}
                      regras={regrasPagamento}
                    >
                      <div className="row justify-content-center align-items-center">
                        <div className="col-md-5">
                          <AnimacaoCartaoDeCredito />
                          <p className="h3 mb-3 text-white text-center">
                            <Icone nome="credit-card" className="me-3" />
                            Pagamento da emissão e envio do certificado
                          </p>
                          <p className="lead mb-4 text-muted text-center">
                            Você pode selecionar os métodos de envio disponíveis
                            para seu endereço abaixo. Selecionando o método será
                            automaticamente calculado o valor e também o prazo
                            para emissão e envio do seu certificado.
                          </p>
                        </div>
                        <div className="col-md-4">
                          <div className="bg-white rounded p-3">
                            <Contexto>
                              {({ values }) => (
                                <React.Fragment>
                                  {values['frete'] && fretes && (
                                    <p className="animate__animated animate__fadeIn fs-1 fw-bold text-center">
                                      {fretes[values['frete']].price.moeda()}
                                      <small className="d-block text-secondary fs-6">
                                        Prazo de{' '}
                                        {fretes[values['frete']].deadline} dias
                                      </small>
                                    </p>
                                  )}
                                </React.Fragment>
                              )}
                            </Contexto>
                            <Selecao
                              name="frete"
                              rotulo="Modalidade do frete"
                              opcoes={FRETES}
                            />
                            <Selecao
                              name="bandeira"
                              rotulo="Bandeira do cartão"
                              opcoes={BANDEIRAS}
                            />
                            <Campo
                              name="nome"
                              rotulo="Nome completo como no cartão"
                            />
                            <Campo name="numero" rotulo="Número do cartão" />
                            <div className="row">
                              <div className="col-md-6">
                                <Campo
                                  name="vencimento"
                                  rotulo="Dt. de vencimento"
                                  mascara={MASCARA_DATA_VENCIMENTO}
                                  semMargem
                                />
                              </div>
                              <div className="col-md-6">
                                <Campo
                                  name="codigo"
                                  rotulo="Cód. de segurança"
                                  semMargem
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="hstack flex-column flex-sm-row gap-3 mx-auto justify-content-center mt-5">
                        <Botao lg type="submit">
                          Configurar meu certificado
                        </Botao>
                        <Botao
                          lg
                          tema="outline-light"
                          onClick={() => setPasso(1)}
                        >
                          Verificar meus dados
                        </Botao>
                      </div>
                    </Form>
                  </Passo>
                  <Passo chave={3}>
                    <div className="row justify-content-center">
                      <div className="col-md-5">
                        <Form onSubmit={enviarCertificado}>
                          <p className="h3 mb-3 text-white text-center">
                            <Icone nome="gear" className="me-3" />
                            Configurações do certificado
                          </p>
                          <p className="lead mb-4 text-muted text-center">
                            Aqui você pode alterar algumas configurações em seu
                            certificado antes de sua emissão. Fique atento pois{' '}
                            <strong>
                              não é possível alterar posteriormente
                            </strong>
                            .
                          </p>
                          <div className="bg-white rounded p-3">
                            <Selecao
                              name="denominacao"
                              rotulo="Qual denominação prefere que apareça em seu certificado?"
                              opcoes={
                                curso.carga_horaria < 180
                                  ? DENOMINACOES_DO_CERTIFICADO.filter(
                                      (denominacao) => denominacao.valor != 1
                                    )
                                  : DENOMINACOES_DO_CERTIFICADO.filter(
                                      (denominacao) => denominacao.valor != 7
                                    )
                              }
                            />
                            <CaixaDeSelecao
                              name="modalidade"
                              rotulo="Imprimir modalidade EAD junto ao nome do curso"
                            />
                            <CaixaDeSelecao
                              name="carga_horaria"
                              rotulo="Imprimir carga horária nos módulos"
                            />
                            <CaixaDeSelecao
                              name="aproveitamento"
                              rotulo="Imprimir aproveitamento nos módulos"
                            />
                            <CaixaDeSelecao
                              name="frequencia"
                              rotulo="Imprimir frequência nos módulos"
                              semMargem
                            />
                          </div>
                          <div className="hstack flex-column flex-sm-row gap-3 mx-auto justify-content-center mt-5">
                            <Botao lg type="submit">
                              {formato == 'digital'
                                ? 'Emitir meu certificado'
                                : 'Pagar e emitir meu certificado'}
                            </Botao>
                            {formato == 'digital' && (
                              <Botao
                                lg
                                tema="outline-light"
                                onClick={() => setPasso(1)}
                              >
                                Verificar meus dados
                              </Botao>
                            )}
                            {formato == 'fisico' && (
                              <Botao
                                lg
                                tema="outline-light"
                                onClick={() => setPasso(2)}
                              >
                                Verificar dados do cartão
                              </Botao>
                            )}
                          </div>
                        </Form>
                      </div>
                    </div>
                  </Passo>
                  <Passo chave={4}>
                    <div className="row justify-content-center">
                      <div className="col-md-5">
                        <div className="d-flex flex-column justify-content-center">
                          <div className="d-flex justify-content-center mb-5">
                            <AnimacaoConfirmacao />
                          </div>
                          {formato == 'digital' ? (
                            <p className="lead text-muted text-center">
                              Seu certificado já foi gerado! Você pode fazer o
                              download dele logo abaixo.
                            </p>
                          ) : (
                            <React.Fragment>
                              <p className="lead text-muted text-center">
                                A versão digital do seu certificado já foi
                                gerada e você pode fazer o download dela
                                clicando no botão abaixo!{' '}
                                <span className="text-white">
                                  A versão física já foi solicita para emissão
                                  pela nossa equipe.
                                </span>
                              </p>
                            </React.Fragment>
                          )}
                          <BotaoLink
                            lg
                            tema="success"
                            to={url}
                            icone="download-cloud-fill"
                            target="_blank"
                          >
                            Download do certificado
                          </BotaoLink>
                          <BotaoLink
                            lg
                            tema="outline-light"
                            className="mt-3"
                            to="/estudar"
                          >
                            Voltar para o início
                          </BotaoLink>
                        </div>
                      </div>
                    </div>
                  </Passo>
                </PassoAPasso>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Adicionar;
